"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { DeckGL } from "@deck.gl/react";
import { H3HexagonLayer } from "@deck.gl/geo-layers";
import { GeoJsonLayer } from "@deck.gl/layers";
import { WebMercatorViewport } from '@deck.gl/core';
import { Map } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { MapLayer } from "@/lib/types";
import { fetchAdministrativeData } from "@/lib/db/queries";
import { createColorScale, type LayerStyle, getLegendData } from "@/lib/map-styles";
import { MapLegend } from "@/components/map-legend";

import {
  Circle,
  LineSegment,
  Hexagon,
  Rectangle,
  Shapes,
  Stack,
  CaretUp,
  CaretDown,
  Compass,
  MapPin,
  ShieldCheck, // Added missing import based on your code logic
  House,       // Added missing import
  ArrowUpRight // Added missing import
} from "@phosphor-icons/react";

if (typeof window !== "undefined") {
  const originalError = console.error;
  console.error = (...args) => {
    if (typeof args[0] === "string" && args[0].includes("maxTextureDimension2D")) return;
    originalError(...args);
  };
}
// --- HELPER: Convert Hex to RGB [r, g, b] for DeckGL ---
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result 
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] 
    : [0, 0, 0];
};

const DEFAULT_LAYER_DATA = {
  type: "FeatureCollection",
  features: [{
    type: "Feature",
    properties: { name: "Southern Manitoba" },
    geometry: { type: "Polygon", coordinates: [[[-98.3, 48.8], [-98.3, 50.6], [-96, 50.6], [-96, 48.8], [-98.3, 48.8]]] }
  }]
};

const DEFAULT_VIEW_STATE = {
  longitude: -97.15, latitude: 49.7, zoom: 8, pitch: 45, bearing: 0,
};

const datasetLabels: Record<string, string> = {
  esa_worldcover_2021: "ESA WorldCover 2021 (Land Cover)",
  manitoba_dem: "Digital Elevation Model (DEM)",
  slope: "Slope (Degrees)",
  d8_accum: "Flow Accumulation (D8)",
  hand: "Height Above Nearest Drainage (HAND)",
  twi: "Topographic Wetness Index (TWI)",
  canada_population_2020: "Population Density (2020)",
  historical_flood_event_0: "Historical Flood Mask",
};

const phaseMap: Record<string, string> = {
  h3_cells: "Analysis",
  geojson: "Analysis",      // S2 results often use the geojson type
  search_area: "Analysis",
  roads: "Impact",          // Road Impact tool
  facilities: "Impact",     // Critical Facilities tool
  isolated_h3: "Impact",    // Isolated Communities tool
  evacuation_route: "Response",
  shelters: "Response",
  // Add this fallback for safety:
  "Analysis Result": "Impact" 
};

export function FloodMap({ layers }: { layers: MapLayer[] }) {
  const [adminLayer, setAdminLayer] = useState<any>(null);
  const [mounted, setMounted] = useState(false);
  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>({});
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(true);

  useEffect(() => {
    setMounted(true);
    fetchAdministrativeData().then(data => setAdminLayer(data)).catch(err => console.error(err));
  }, []);

  const validLayers = useMemo(() => layers.filter((layer) => {
    if (!layer.cachedData) return false;
    return layer.cachedData.type === "FeatureCollection" ? (layer.cachedData.features?.length ?? 0) > 0 : Array.isArray(layer.cachedData) ? layer.cachedData.length > 0 : false;
  }), [layers]);

  // Unified bounds extractor for FlyTo
  const getBoundsFromData = (data: any) => {
    const points: Array<{ lat: number; lon: number }> = [];
    if (data?.type === "FeatureCollection") {
      data.features?.forEach((f: any) => {
        const coords = f.geometry?.coordinates;
        if (!coords) return;
        if (f.geometry.type === "Point") points.push({ lon: coords[0], lat: coords[1] });
        else if (f.geometry.type === "Polygon") coords[0].forEach((c: any) => points.push({ lon: c[0], lat: c[1] }));
        else if (f.geometry.type === "MultiPolygon") coords.forEach((poly: any) => poly[0].forEach((c: any) => points.push({ lon: c[0], lat: c[1] })));
        else if (f.geometry.type === "LineString") coords.forEach((c: any) => points.push({ lon: c[0], lat: c[1] }));
      });
    } else if (Array.isArray(data)) {
        data.forEach((item: any) => { if (item.location) points.push({ lat: item.location.lat, lon: item.location.lon }); });
    }
    return points;
  };

  // FlyTo Logic
  const flyToData = useCallback((data: any) => {
    const points = getBoundsFromData(data);
    if (points.length === 0) return;
    const lons = points.map(p => p.lon);
    const lats = points.map(p => p.lat);
    const viewport = new WebMercatorViewport({ width: window.innerWidth, height: window.innerHeight, ...viewState })
      .fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]], { padding: 80 });
    setViewState(prev => ({ ...prev, ...viewport, transitionDuration: 1000 }));
  }, [viewState]);

  // Auto-zoom on new layers
  const dataBounds = useMemo(() => {
    const allPoints: Array<{ lat: number; lon: number }> = [];
    validLayers.forEach(l => allPoints.push(...getBoundsFromData(l.cachedData)));
    if (allPoints.length === 0) return null;
    return { lons: allPoints.map(p => p.lon), lats: allPoints.map(p => p.lat) };
  }, [validLayers]);

  useEffect(() => {
    if (!dataBounds) return;
    const viewport = new WebMercatorViewport({ width: window.innerWidth * 0.5, height: window.innerHeight * 0.85, ...viewState })
      .fitBounds([[Math.min(...dataBounds.lons), Math.min(...dataBounds.lats)], [Math.max(...dataBounds.lons), Math.max(...dataBounds.lats)]], { padding: 80, maxZoom: 11 });
    setViewState(prev => ({ ...prev, ...viewport, transitionDuration: 1000 }));
  }, [dataBounds]);

  // Legend Calculation
  const legendData = useMemo(() => {
    for (let i = validLayers.length - 1; i >= 0; i--) {
      const layer = validLayers[i];
      if (visibilityMap[layer.id] !== false) {
        const spec = layer.specification?.layers?.[0];
        const styleConfig = spec?.style as LayerStyle;
        if (spec && styleConfig?.fillColor && !Array.isArray(styleConfig.fillColor)) {
             const data = Array.isArray(layer.cachedData) ? layer.cachedData : layer.cachedData.features || [];
             return getLegendData(data, styleConfig);
        }
      }
    }
    return null;
  }, [validLayers, visibilityMap]);

  // Icon logic with the requested Rectangle duotone for polygons
  const getGeometryIcon = (resultType?: string, data?: any, label?: string, style) => {
    // Check if the label contains "S2" or "H3" to treat it as a grid
    const isGrid = resultType?.includes("cells") || 
                   resultType?.includes("hexagon") || 
                   label?.toUpperCase().includes("S2") || 
                   label?.toUpperCase().includes("H3");

    if (isGrid) {
      // Check for "Isolated" status to show the Shield icon (Impact phase)
      const isIsolated = data?.features?.[0]?.properties?.isolation_status === "Isolated Safe Island";
      if (isIsolated || resultType === "isolated_h3") {
        return <ShieldCheck size={18} color="#f59e0b" weight="duotone" className="flex-shrink-0" />;
      }
      // Otherwise show the standard grid icon
      return <Hexagon size={18} color="#f59e0b" weight="duotone" className="flex-shrink-0" />;
    }

    // Handle specific tool result types
    if (resultType === "search_area") {
      return <Rectangle size={18} color="#8e44ad" weight="duotone" className="flex-shrink-0" />;
    }

    if (resultType === "roads" || label.toLowerCase().includes("segments")) {
        // Use the color defined in the layer style for the icon
        const color = style?.lineColor ? 
            `rgb(${style.lineColor[0]}, ${style.lineColor[1]}, ${style.lineColor[2]})` : 
            "#0f172a";
            
        return <LineSegment size={18} color={color} weight="bold" className="flex-shrink-0" />;
    }

    if (resultType === "facilities") {
      return <MapPin size={18} color="#dc2626" weight="fill" className="flex-shrink-0" />;
    }

    if (resultType === "buildings") {
      return <House size={18} color="#64748b" weight="duotone" className="flex-shrink-0" />;
    }

    if (resultType === "evacuation_route") {
      return <ArrowUpRight size={18} color="#16a34a" weight="bold" className="flex-shrink-0" />;
    }
    
    if (resultType === "shelters") {
      return <House size={18} color="#2563eb" weight="fill" className="flex-shrink-0" />;
    }

    // --- FIX START: Define geomType based on the data ---
    let geomType = "Unknown";
    if (data?.features?.length > 0) {
        geomType = data.features[0].geometry?.type;
    } else if (Array.isArray(data) && data.length > 0) {
        geomType = data[0].geometry?.type;
    }
    // --- FIX END ---

    switch (geomType) {
      case "Point": 
        return <Circle size={18} color="#e11d48" weight="duotone" className="flex-shrink-0" />;
      case "LineString": 
        return <LineSegment size={18} color="#2563eb" weight="duotone" className="flex-shrink-0" />;
      case "Polygon":
      case "MultiPolygon": 
        return <Rectangle size={18} color="#4f46e5" weight="duotone" className="flex-shrink-0" />;
      default: 
        return <Shapes size={18} color="#16a34a" weight="duotone" className="flex-shrink-0" />;
    }
  };

  const toggleLayer = (layerId: string) => setVisibilityMap(prev => ({ ...prev, [layerId]: !prev[layerId] }));

  const deckLayers = useMemo(() => {
    const layersList = [
      adminLayer ? new GeoJsonLayer({
        id: "admin-level-6", data: adminLayer, visible: visibilityMap["admin-level-6"] ?? true,
        filled: true, stroked: true, getFillColor: [100, 150, 200, 30], getLineColor: [40, 60, 100, 255], getLineWidth: 1, lineWidthUnits: 'pixels'
      }) : null,
      new GeoJsonLayer({
        id: "default-bounds", data: DEFAULT_LAYER_DATA, visible: !adminLayer,
        filled: true, getFillColor: [200, 200, 200, 30], getLineColor: [100, 100, 100, 150], getLineWidth: 2
      }),
    ];

    const dataLayers = validLayers.flatMap((artifact) => {
      // Map through each sub-layer defined in the backend specification
      return artifact.specification.layers.map((layerSpec, specIdx) => {
        // This ID must match the one used in your Layers Menu checkbox logic
        const layerId = `${artifact.id}-${specIdx}`;
        
        // FIX: Check visibility for the specific sub-layer, not the whole artifact
        const isVisible = visibilityMap[layerId] ?? layerSpec.visible;
        
        const resultType = layerSpec.source.result_type;
        const features = artifact.cachedData?.features || (Array.isArray(artifact.cachedData) ? artifact.cachedData : []);
        
        // Generate a color scale specific to THIS layer's field and palette 
        // (e.g., 'hand' with 'blues', or 'twi' with 'greens')
        const colorScale = createColorScale(features, layerSpec.style);

        // Handle H3 Hexagons (The "Hexagon Sign" requirement)
        if (resultType === "h3_cells") {
          return new H3HexagonLayer({
            id: layerId,
            data: features,
            visible: isVisible,
	    opacity: 0.9,
            pickable: true,
            stroked: true,
            filled: true,
            extruded: true, // Enable 3D height
            // --- ADD THIS BLOCK TO REMOVE DULL SHADOWS ---
            material: {
              ambient: 1.0,         // Full brightness from all angles
              diffuse: 0.0,         // No "sunlight" reflection
              shininess: 0          // No glossy highlights
            },
            // ----------------------------------------------
            getHexagon: (d) => d.h3_cell || d.id || d.properties?.h3_index,
            getFillColor: (d) => colorScale(d),
            getLineColor: [255, 255, 255, 50], // Subtle white border
            getLineWidth: 1,
            // Keep height tied to probability for visual context, even if color changes
            getElevation: (d) => (d.flood_probability ?? 0) * 500,
            elevationScale: 20,
            updateTriggers: {
              getFillColor: [isVisible], // Refresh color if visibility changes
            },
          });
        }

        // Handle Standard GeoJSON (Search BBoxes or Road Vectors)
	return new GeoJsonLayer({
	  id: layerId,
	  data: artifact.cachedData,
	  visible: isVisible,
	  filled: layerSpec.style?.filled ?? true,
	  stroked: true,
  
	  // 1. Fill Logic: Only fill polygons (hexagons/search boxes)
	  //getFillColor: (d) => (d.geometry?.type.includes("Polygon")) ? colorScale(d) : [0, 0, 0, 0],
	  getFillColor: (d) => {
	    const type = d.geometry?.type;
	    // Fill if it's a Polygon OR a Point (Risk Cell)
	    if (type?.includes("Polygon") || type === "Point") {
	        return colorScale(d);
	    }
	    return [0, 0, 0, 0];
	  },	  

	  // 2. Line Color Logic
	  getLineColor: (d) => {
            // A. Check for properties coming from Backend (Data-Driven Styling)
            if (d.properties?.stroke) {
                const rgb = hexToRgb(d.properties.stroke);
                // If opacity exists (0-1), map to 0-255. Default to 255 (opaque)
                const opacity = d.properties['stroke-opacity'] !== undefined 
                    ? d.properties['stroke-opacity'] * 255 
                    : 255;
                return [...rgb, opacity];
            }
	    if (resultType === "search_area") return [142, 68, 173, 255]; // Purple BBox
    
	    // Use the color from backend (e.g., the dark slate we set for Impacted Roads)
	    if (layerSpec.style?.lineColor) return layerSpec.style.lineColor;
    
	    // Default thin white outline for flood cells
	    return [255, 255, 255, 150]; 
	  },

      // 3. Line Width Logic
      getLineWidth: (d) => {
        if (resultType === "search_area") return 4;
            // Check properties first
            if (d.properties?.['stroke-width']) {
                return d.properties['stroke-width'];
            }
    
        // If the backend identifies this as a roads layer, we want it thick.
        // We use 6 as a default if the backend didn't specify.
        if (resultType === "roads") {
            return layerSpec.style?.lineWidth || 6;
        }
    
        return layerSpec.style?.lineWidth || 1;
      },

      // 4. Advanced Visibility Logic
      lineWidthUnits: 'pixels',
      // This ensures that even when you zoom way out, the road lines 
      // stay at least 3 pixels wide so they remain visible on the map.
      lineWidthMinPixels: 2, 

      // 5. Point Radius Logic (Crucial for Risk Centroids)
      getPointRadius: (d) => layerSpec.style?.circleRadius || 5,
      pointRadiusUnits: 'pixels', // Ensures they stay visible at any zoom
      pointRadiusMinPixels: 3,
	    
      pickable: true,
      autoHighlight: true,
      // Adds a white "glow" when you hover over a road segment
      highlightColor: [255, 255, 255, 100], 
    });
      });
    }).filter(Boolean);

    return [...layersList, ...dataLayers];
  }, [validLayers, visibilityMap, adminLayer]);

  if (!mounted) return <div className="flex h-full items-center justify-center"><div>Loading...</div></div>;

  return (
    <div className="relative w-full h-full bg-slate-50">
      <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 w-max max-w-[340px]">
        <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)}>
          <div className="flex items-center gap-2">
            <Stack size={20} className="text-violet-600" weight="duotone" />
            <span className="text-sm font-semibold">Layers</span>
          </div>
          {isLayerMenuOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </div>

        {isLayerMenuOpen && (
          <div className="p-3 pt-0 space-y-2 max-h-[50vh] overflow-y-auto min-w-[280px]">
            <div className="h-px bg-slate-100 mb-2"></div>
            
            {/* Manitoba Admin Boundaries with FlyTo */}
            {adminLayer && (
              <div className="flex items-center justify-between group hover:bg-slate-50 p-1 rounded transition-colors">
                <label className="flex items-center space-x-2 text-sm cursor-pointer flex-1">
                    <input type="checkbox" checked={visibilityMap["admin-level-6"] ?? true} onChange={() => toggleLayer("admin-level-6")} className="rounded text-violet-600" />
                    <div className="flex items-center gap-2">
                        <Rectangle size={18} color="#4f46e5" weight="duotone" className="flex-shrink-0" />
                        <span className="font-medium text-slate-700">Manitoba Admin Boundaries</span>
                    </div>
                </label>
                <button onClick={() => flyToData(adminLayer)} className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-violet-600 transition-all" title="Locate Manitoba"><Compass size={16} weight="bold" /></button>
              </div>
            )}

        {/* Dynamic Layers */}
        {validLayers.flatMap((artifact) => {
            // We iterate through the 'layers' array sent by the backend
            return artifact.specification.layers.map((layerSpec, specIdx) => {
                // 1. Calculate the feature count from the cached data
                const features = artifact.cachedData?.features || 
                (Array.isArray(artifact.cachedData) ? artifact.cachedData : []);
                const featureCount = features.length;
                
                // 2. Determine the Phase based on result_type
                const resultType = layerSpec.source.result_type;
                const phase = phaseMap[resultType] || "Analysis";
        
                // We create a unique ID for this specific variable (e.g. "runID-0", "runID-1")
                const subLayerId = `${artifact.id}-${specIdx}`;
        
                // Use the 'visible' default from the backend, or the user's manual toggle
                const isVisible = visibilityMap[subLayerId] ?? layerSpec.visible;
        
                // 'Flood Probability (H3 1000m)', 'HAND (H3 1000m)', etc.
                const label = layerSpec.name || "Analysis Result";

                return (
                    <div key={subLayerId} className="flex items-center justify-between group hover:bg-slate-50 p-1 rounded transition-colors">
                        <label className="flex items-center space-x-2 text-sm cursor-pointer flex-1 min-w-0">
                            <input 
                                type="checkbox" 
                                checked={isVisible} 
                                onChange={() => toggleLayer(subLayerId)} 
                                className="rounded text-violet-600" 
                            />
                            <div className="flex items-center gap-2 min-w-0">
                                {/* This will now show the Hexagon icon for all 4 variables */}
                    {/* Pass label to help identify S2/H3 grids */}
                                {getGeometryIcon(resultType, artifact.cachedData, label, layerSpec.style)}
                                <div className="flex flex-col min-w-0">
                                    <span className="font-medium text-slate-700 leading-tight truncate">
                                        {label}
                                    </span>
                                    <span className="text-[10px] text-slate-400">
                                        {phase} • {featureCount} {featureCount === 1 ? 'feature' : 'features'}
                                    </span>
                                </div>
                            </div>
                        </label>
                        <button 
                            onClick={() => flyToData(artifact.cachedData)} 
                            className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-violet-600 transition-all"
                            title="Zoom to layer"
                        >
                            <Compass size={16} weight="bold" />
                        </button>
                    </div>
                );
            });
        })}


          </div>
        )}
      </div>

      {legendData && <MapLegend title={legendData.title} type={legendData.type} items={legendData.items} />}

      <DeckGL
        viewState={viewState} onViewStateChange={({ viewState }) => setViewState(viewState)} controller={true} layers={deckLayers}
        getTooltip={({ object }) => {
          if (!object) return null;
          const props = object.properties || object;
          const cellId = props.h3_index || props.zone_id || (typeof props.id === 'string' ? props.id : null);
          // --- FIX: Filter out internal visual properties ---
          const excludedKeys = [
            'geometry', 'id', 'h3_index', 'zone_id', 'u', 'v', 'w', 'x', 'y', 'z', '_zone_id_label',
            'stroke', 'stroke-width', 'stroke-opacity' // Added these to remove from Popup
          ];

          const content = Object.entries(props)
            .filter(([k]) => !excludedKeys.includes(k))
            .map(([k, v]) => {
                let d = (typeof v === 'number') ? v.toLocaleString(undefined, { maximumFractionDigits: 4 }) : v;
                return `<div class="flex justify-between gap-4"><span class="text-gray-400">${k}:</span><span class="font-mono font-bold">${d}</span></div>`;
            }).join('');
          const header = cellId && typeof cellId === 'string' ? `<div class="mb-2 pb-1 border-b border-slate-700 font-mono text-blue-300 font-bold text-center">${cellId}</div>` : '';
          return {
            html: `<div class="p-2 text-xs min-w-[150px]">${header}${content || 'No data properties'}</div>`,
            style: { backgroundColor: '#0f172a', color: 'white', borderRadius: '6px', border: '1px solid #334155', zIndex: 100 }
          };
        }}
      >
        <Map mapLib={maplibregl} mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json" />
      </DeckGL>
    </div>
  );
}
